// src/utils/cacheBuster.js
// ============================================================
// 📦 사이트 데이터 초기화 + 캐시 버스터 통합 유틸리티
// ============================================================
//
// 🎯 목적:
//   - 앱 버전 변경 시 자동 캐시 클리어
//   - 로그아웃 시 사용자 데이터 완전 삭제
//   - 개발/디버깅 시 저장소 초기화
//
// 📋 지원하는 저장소:
//   1. Cookies (document.cookie)
//   2. localStorage / sessionStorage
//   3. SharedStorage API (Chrome 117+)
//   4. CacheStorage (Service Worker 캐시)
//   5. ServiceWorker + Push 구독
//   6. IndexedDB
//   7. OPFS (Origin Private File System)
//   8. Storage Buckets API
//   9. 서버 측 Clear-Site-Data 헤더
//
// 🔧 사용 예시:
//   - 앱 시작 시: await checkAndClearCache()
//   - 로그아웃 시: await clearUserDataOnly()
//   - 강제 초기화: await nukeAll()
//   - 캐시만 삭제: await clearCacheAndSw()
//
// ============================================================

import { logError, logDebug, logInfo } from '@/utils/logHelper';

/* =========================================================
 * 🔧 상수 & 설정
 * ======================================================= */

/**
 * 빌드 버전 (webpack DefinePlugin 또는 .env에서 주입)
 * - 버전 변경 감지에 사용
 * - 예: "2024-01-15T10:30:00Z" 또는 "1.2.3"
 */
const BUILD_VERSION = process.env.VUE_APP_BUILD_TIME || 'dev';

/**
 * 버전 저장 키 (localStorage)
 * - 이전 버전과 비교하여 캐시 클리어 결정
 */
const STORAGE_KEY = 'app_version';

/**
 * 서버 측 Clear-Site-Data 엔드포인트
 * - 서버에서 Clear-Site-Data 헤더를 반환하는 API
 * - 헤더 예: Clear-Site-Data: "cache", "cookies", "storage"
 */
const CLEAR_ENDPOINT = '/api/v1/auth/clear-site-data';

/**
 * 앱에서 사용하는 IndexedDB 이름 목록
 * - clearAppIndexedDB()에서 이 목록의 DB만 삭제
 * - 필요 시 추가: ['fileManagerDB', 'myAppDB', ...]
 */
const APP_IDB_NAMES = Object.freeze(['fileManagerDB']);

/**
 * localStorage에서 보존할 키 목록
 * - clearLocal() 실행 시에도 삭제되지 않음
 * - 예: 버전 키, 사용자 설정 등
 */
const PRESERVE_KEYS = Object.freeze([STORAGE_KEY]);

/* =========================================================
 * 🚀 버전 체크 & 자동 캐시 클리어
 * ======================================================= */

/**
 * 앱 시작 시 호출 - 버전 변경 감지하면 nukeAll 실행 후 새로고침
 * 
 * @description
 * 1. localStorage에서 이전 버전 확인
 * 2. 현재 빌드 버전과 비교
 * 3. 다르면 nukeAll() 실행 후 새로고침
 * 
 * @returns {Promise<boolean>} 새로고침 예정 여부 (true면 곧 새로고침됨)
 * 
 * @example
 * // main.js 또는 App.vue onMounted에서
 * import { checkAndClearCache } from '@/utils/cacheBuster';
 * 
 * async function bootstrap() {
 *   const willReload = await checkAndClearCache();
 *   if (willReload) return; // 새로고침 예정이면 초기화 중단
 *   
 *   // 나머지 앱 초기화...
 *   app.mount('#app');
 * }
 * 
 * @example
 * // App.vue에서 사용
 * onMounted(async () => {
 *   if (await checkAndClearCache()) return;
 *   await mainStore.checkAuthAction();
 * });
 */
export async function checkAndClearCache() {
  const storedVersion = localStorage.getItem(STORAGE_KEY);

  // 버전 동일하면 아무것도 안 함
  if (storedVersion === BUILD_VERSION) {
    logDebug(`버전 동일: ${BUILD_VERSION}`);
    return false;
  }

  logInfo(`🔄 새 버전 감지: ${storedVersion || '(없음)'} → ${BUILD_VERSION}`);

  // ⚠️ 무한 루프 방지: nukeAll 전에 버전 먼저 저장
  // (nukeAll이 localStorage를 삭제하지만, PRESERVE_KEYS에 포함되어 보존됨)
  localStorage.setItem(STORAGE_KEY, BUILD_VERSION);

  try {
    await nukeAll({
      useServerHeader: false,    // 서버 요청은 생략 (빠른 시작)
      dropFileManagerDB: true,   // fileManagerDB 삭제
      dropAllIndexedDB: false,   // 다른 IndexedDB는 유지
      autoReload: false,         // 여기서 직접 새로고침
    });

    logInfo('✅ nukeAll 완료, 새로고침 예정');
    
    // 약간의 딜레이 후 새로고침 (로그 출력 보장)
    setTimeout(() => {
      window.location.reload(true); // true = 캐시 무시
    }, 100);

    return true;
  } catch (err) {
    logError('nukeAll 실패', err);
    // 실패해도 새로고침 (최소한 브라우저 캐시는 무시)
    window.location.reload(true);
    return true;
  }
}

/* =========================================================
 * 🍪 쿠키 관련
 * ======================================================= */

/**
 * 모든 쿠키를 공격적으로 삭제
 * 
 * @description
 * 쿠키 삭제가 어려운 이유:
 * - 쿠키는 도메인/경로/보안 속성에 따라 다르게 설정됨
 * - 설정 시 사용한 정확한 조합을 알아야 삭제 가능
 * 
 * 이 함수는 가능한 모든 조합을 시도:
 * - 여러 경로: /, '', 현재 경로
 * - 여러 도메인: 현재 호스트, .호스트, 루트 도메인
 * - 보안 속성: secure, samesite 등
 * 
 * @returns {Promise<{cleared: number}>} 삭제 시도한 쿠키 수
 * 
 * @example
 * // 로그아웃 시
 * await clearCookiesAggressive();
 * 
 * @example
 * // 결과 확인
 * const result = await clearCookiesAggressive();
 * console.log(`${result.cleared}개 쿠키 삭제 시도`);
 */
export async function clearCookiesAggressive() {
  const cookies = document.cookie.split(';');
  
  // 쿠키가 없으면 조기 종료
  if (cookies.length === 0 || (cookies.length === 1 && !cookies[0].trim())) {
    logDebug('삭제할 쿠키 없음');
    return { cleared: 0 };
  }

  const host = window.location.hostname;
  
  // 시도할 경로 목록
  const paths = ['/', '', window.location.pathname];
  
  // 시도할 도메인 목록
  const domains = [host, `.${host}`, ''];
  
  // 루트 도메인 추가 (예: sub.example.com → example.com)
  const hostParts = host.split('.');
  if (hostParts.length > 2) {
    const rootDomain = hostParts.slice(-2).join('.');
    domains.push(rootDomain, `.${rootDomain}`);
  }

  let cleared = 0;

  for (const cookie of cookies) {
    const eqPos = cookie.indexOf('=');
    const name = (eqPos > -1 ? cookie.substring(0, eqPos) : cookie).trim();
    
    if (!name) continue;

    // 모든 경로/도메인 조합 시도
    for (const path of paths) {
      for (const domain of domains) {
        const domainPart = domain ? `; domain=${domain}` : '';
        const parts = [
          `${name}=`,                                    // 값 비우기
          'expires=Thu, 01 Jan 1970 00:00:00 GMT',      // 과거 날짜로 만료
          `path=${path || '/'}`,
        ];
        
        // 다양한 보안 속성 조합 시도
        document.cookie = parts.join('; ') + domainPart;
        document.cookie = parts.join('; ') + domainPart + '; secure';
        document.cookie = parts.join('; ') + domainPart + '; samesite=lax';
        document.cookie = parts.join('; ') + domainPart + '; secure; samesite=none';
      }
    }
    cleared++;
  }

  logDebug(`쿠키 ${cleared}개 삭제 시도`);
  return { cleared };
}

/* =========================================================
 * 💾 웹 스토리지
 * ======================================================= */

/**
 * localStorage 삭제 (보존 키 제외)
 * 
 * @description
 * PRESERVE_KEYS에 정의된 키는 삭제되지 않음
 * - 기본적으로 'app_version' 키가 보존됨
 * - 무한 새로고침 루프 방지
 * 
 * @returns {Promise<{cleared: number}>} 실제 삭제된 항목 수
 * 
 * @example
 * await clearLocal();
 * 
 * @example
 * // 특정 키 보존이 필요하면 PRESERVE_KEYS 상수 수정
 * const PRESERVE_KEYS = Object.freeze([
 *   STORAGE_KEY,
 *   'user_preferences',
 *   'theme',
 * ]);
 */
export async function clearLocal() {
  // 1. 보존할 값들 백업
  const preserved = {};
  for (const key of PRESERVE_KEYS) {
    const val = localStorage.getItem(key);
    if (val !== null) preserved[key] = val;
  }

  const count = localStorage.length;
  
  // 2. 전체 삭제
  localStorage.clear();

  // 3. 보존 키 복원
  for (const [key, val] of Object.entries(preserved)) {
    localStorage.setItem(key, val);
  }

  logDebug(`localStorage ${count}개 항목 삭제 (${PRESERVE_KEYS.length}개 보존)`);
  return { cleared: count - Object.keys(preserved).length };
}

/**
 * sessionStorage 전체 삭제
 * 
 * @description
 * sessionStorage는 탭/윈도우 단위로 격리되므로
 * 현재 탭의 데이터만 삭제됨
 * 
 * @returns {Promise<{cleared: number}>} 삭제된 항목 수
 */
export async function clearSession() {
  const count = sessionStorage.length;
  sessionStorage.clear();
  
  logDebug(`sessionStorage ${count}개 항목 삭제`);
  return { cleared: count };
}

/**
 * SharedStorage 삭제 (Chrome 117+)
 * 
 * @description
 * Shared Storage API는 크로스-사이트 데이터 공유를 위한 새 API
 * - 주로 광고/분석 용도
 * - 대부분의 앱에서는 사용하지 않음
 * - 지원하지 않는 브라우저에서는 조용히 스킵
 * 
 * @returns {Promise<{supported: boolean} | {cleared: boolean} | {error: string}>}
 */
export async function clearSharedStorage() {
  if (!('sharedStorage' in window)) {
    logDebug('SharedStorage API 미지원');
    return { supported: false };
  }

  try {
    await window.sharedStorage.clear();
    logDebug('SharedStorage 삭제 완료');
    return { cleared: true };
  } catch (err) {
    logError('SharedStorage 삭제 실패', err);
    return { error: err.message };
  }
}

/* =========================================================
 * 📦 캐시 & Service Worker
 * ======================================================= */

/**
 * CacheStorage 전체 삭제
 * 
 * @description
 * Service Worker가 캐시한 리소스 삭제
 * - API 응답 캐시
 * - 정적 파일 캐시 (PWA)
 * - 이미지/폰트 캐시
 * 
 * @returns {Promise<{supported: boolean} | {cleared: number, names: string[]} | {error: string}>}
 * 
 * @example
 * // PWA 업데이트 후 오래된 캐시 제거
 * const result = await clearCacheStorage();
 * if (result.cleared > 0) {
 *   console.log('삭제된 캐시:', result.names);
 * }
 */
export async function clearCacheStorage() {
  if (!('caches' in window)) {
    logDebug('CacheStorage API 미지원');
    return { supported: false };
  }

  try {
    const keys = await caches.keys();
    await Promise.all(keys.map(key => caches.delete(key)));
    
    logDebug(`CacheStorage ${keys.length}개 삭제: ${keys.join(', ')}`);
    return { cleared: keys.length, names: keys };
  } catch (err) {
    logError('CacheStorage 삭제 실패', err);
    return { error: err.message };
  }
}

/**
 * Service Worker 해제 및 Push 구독 취소
 * 
 * @description
 * PWA 관련 모든 백그라운드 기능 해제:
 * 1. Push 알림 구독 취소
 * 2. 대기 중인 SW에 SKIP_WAITING 메시지 전송
 * 3. SW 등록 해제
 * 
 * @returns {Promise<{supported: boolean} | {sw: number, push: number} | {error: string}>}
 * 
 * @example
 * // 앱 완전 초기화 시
 * const result = await unregisterServiceWorkersAndPush();
 * console.log(`SW ${result.sw}개, Push ${result.push}개 해제`);
 */
export async function unregisterServiceWorkersAndPush() {
  if (!('serviceWorker' in navigator)) {
    logDebug('ServiceWorker API 미지원');
    return { supported: false };
  }

  const results = { sw: 0, push: 0 };

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const reg of registrations) {
      // 1. Push 구독 취소
      try {
        const pushSub = await reg.pushManager?.getSubscription?.();
        if (pushSub) {
          await pushSub.unsubscribe();
          results.push++;
        }
      } catch (e) {
        logDebug('Push 구독 취소 실패', e);
      }

      // 2. 대기 중인 SW 활성화 (SKIP_WAITING)
      if (reg.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      }

      // 3. SW 해제
      const ok = await reg.unregister();
      if (ok) results.sw++;
    }

    logDebug(`ServiceWorker ${results.sw}개, Push ${results.push}개 해제`);
    return results;
  } catch (err) {
    logError('ServiceWorker 해제 실패', err);
    return { error: err.message };
  }
}

/* =========================================================
 * 📂 파일계 저장소
 * ======================================================= */

/**
 * OPFS (Origin Private File System) 삭제
 * 
 * @description
 * 브라우저 내 가상 파일 시스템
 * - 대용량 파일 저장 (동영상, 3D 모델 등)
 * - IndexedDB보다 파일 작업에 최적화
 * - Chrome 86+, Safari 15.2+
 * 
 * @returns {Promise<{supported: boolean} | {cleared: number} | {error: string}>}
 * 
 * @example
 * // 업로드된 임시 파일 정리
 * await clearOPFS();
 */
export async function clearOPFS() {
  if (!('storage' in navigator) || !navigator.storage.getDirectory) {
    logDebug('OPFS API 미지원');
    return { supported: false };
  }

  try {
    const root = await navigator.storage.getDirectory();
    let count = 0;

    // 모든 파일/폴더 재귀 삭제
    for await (const [name] of root.entries()) {
      await root.removeEntry(name, { recursive: true });
      count++;
    }

    logDebug(`OPFS ${count}개 항목 삭제`);
    return { cleared: count };
  } catch (err) {
    logError('OPFS 삭제 실패', err);
    return { error: err.message };
  }
}

/**
 * Storage Buckets 삭제
 * 
 * @description
 * Storage Buckets API (Chrome 122+)
 * - 저장소를 논리적 버킷으로 분리
 * - 버킷별 할당량/지속성 설정 가능
 * - 아직 실험적 기능
 * 
 * @returns {Promise<{supported: boolean} | {cleared: number} | {error: string}>}
 */
export async function clearStorageBuckets() {
  if (!('storageBuckets' in navigator)) {
    logDebug('Storage Buckets API 미지원');
    return { supported: false };
  }

  try {
    const buckets = await navigator.storageBuckets.keys();
    let count = 0;

    for (const name of buckets) {
      await navigator.storageBuckets.delete(name);
      count++;
    }

    logDebug(`Storage Buckets ${count}개 삭제`);
    return { cleared: count };
  } catch (err) {
    logError('Storage Buckets 삭제 실패', err);
    return { error: err.message };
  }
}

/* =========================================================
 * 🗄️ IndexedDB
 * ======================================================= */

/**
 * 특정 IndexedDB 삭제
 * 
 * @param {string} dbName - 삭제할 DB 이름
 * @returns {Promise<{supported: boolean} | {deleted: string, blocked?: boolean}>}
 * 
 * @description
 * IndexedDB 삭제 시 주의사항:
 * - 다른 탭에서 DB를 사용 중이면 blocked 상태가 됨
 * - blocked 상태에서도 결국 삭제되지만 지연될 수 있음
 * 
 * @example
 * await clearIndexedDBStores('fileManagerDB');
 * await clearIndexedDBStores('myAppDB');
 */
export function clearIndexedDBStores(dbName) {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      logDebug('IndexedDB API 미지원');
      resolve({ supported: false });
      return;
    }

    const req = indexedDB.deleteDatabase(dbName);

    req.onsuccess = () => {
      logDebug(`IndexedDB "${dbName}" 삭제 완료`);
      resolve({ deleted: dbName });
    };

    req.onerror = () => {
      logError(`IndexedDB "${dbName}" 삭제 실패`, req.error);
      reject(req.error);
    };

    // 다른 탭에서 DB를 사용 중일 때
    req.onblocked = () => {
      logDebug(`IndexedDB "${dbName}" 삭제 차단됨 (다른 탭에서 사용 중)`);
      resolve({ deleted: dbName, blocked: true });
    };
  });
}

/**
 * 앱에서 사용하는 IndexedDB만 삭제
 * 
 * @description
 * APP_IDB_NAMES 상수에 정의된 DB만 삭제
 * - 기본: ['fileManagerDB']
 * - 다른 앱(확장 프로그램 등)의 DB는 보존
 * 
 * @returns {Promise<Array<{deleted: string} | {error: string, db: string}>>}
 * 
 * @example
 * const results = await clearAppIndexedDB();
 * results.forEach(r => {
 *   if (r.error) console.error(`${r.db} 삭제 실패: ${r.error}`);
 * });
 */
export async function clearAppIndexedDB() {
  const results = [];
  
  for (const dbName of APP_IDB_NAMES) {
    try {
      const result = await clearIndexedDBStores(dbName);
      results.push(result);
    } catch (err) {
      results.push({ error: err.message, db: dbName });
    }
  }

  return results;
}

/**
 * 모든 IndexedDB 삭제 (주의!)
 * 
 * @description
 * ⚠️ 주의: 브라우저 확장 프로그램 등 다른 앱의 DB도 삭제됨
 * 
 * indexedDB.databases()는 Chrome/Edge에서만 지원
 * Firefox/Safari에서는 clearAppIndexedDB()로 대체됨
 * 
 * @returns {Promise<{supported: boolean} | {cleared: number, details: Array}>}
 * 
 * @example
 * // 완전 초기화가 필요한 경우만 사용
 * await clearAllIndexedDB();
 */
export async function clearAllIndexedDB() {
  if (!('indexedDB' in window)) {
    return { supported: false };
  }

  // Firefox/Safari는 databases() 미지원
  if (!indexedDB.databases) {
    logDebug('indexedDB.databases() 미지원 - 앱 DB만 삭제');
    return clearAppIndexedDB();
  }

  try {
    const dbs = await indexedDB.databases();
    const results = [];

    for (const db of dbs) {
      if (!db.name) continue;
      
      try {
        await clearIndexedDBStores(db.name);
        results.push({ deleted: db.name });
      } catch (err) {
        results.push({ error: err.message, db: db.name });
      }
    }

    logDebug(`IndexedDB ${results.length}개 삭제 시도`);
    return { cleared: results.length, details: results };
  } catch (err) {
    logError('IndexedDB 목록 조회 실패', err);
    return { error: err.message };
  }
}

/* =========================================================
 * 🌐 서버 측 Clear-Site-Data
 * ======================================================= */

/**
 * 서버에 Clear-Site-Data 요청
 * 
 * @description
 * 서버가 응답 헤더로 Clear-Site-Data를 반환하면
 * 브라우저가 지정된 데이터를 삭제함
 * 
 * 서버 응답 예시:
 * ```
 * HTTP/1.1 200 OK
 * Clear-Site-Data: "cache", "cookies", "storage"
 * ```
 * 
 * Spring Boot 예시:
 * ```java
 * @PostMapping("/clear-site-data")
 * public ResponseEntity<Void> clearSiteData(HttpServletResponse response) {
 *     response.setHeader("Clear-Site-Data", "\"cache\", \"cookies\", \"storage\"");
 *     return ResponseEntity.ok().build();
 * }
 * ```
 * 
 * @param {Object} options
 * @param {string} options.endpoint - API 엔드포인트 (기본: CLEAR_ENDPOINT)
 * @returns {Promise<{success: boolean, status: number} | {error: string}>}
 */
export async function clearViaServer(options = {}) {
  const endpoint = options.endpoint || CLEAR_ENDPOINT;

  try {
    const resp = await fetch(endpoint, {
      method: 'POST',
      credentials: 'include', // 쿠키 포함 (인증 필요 시)
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!resp.ok) {
      throw new Error(`HTTP ${resp.status}`);
    }

    logDebug('서버 Clear-Site-Data 요청 완료');
    return { success: true, status: resp.status };
  } catch (err) {
    logError('서버 Clear-Site-Data 요청 실패', err);
    return { error: err.message };
  }
}

/* =========================================================
 * 💣 통합 함수: nukeAll (핵폭탄)
 * ======================================================= */

/**
 * 모든 저장소 완전 삭제 (핵 옵션)
 * 
 * @description
 * 브라우저의 모든 저장소를 순차적으로 삭제
 * 실행 순서:
 *   1. 쿠키 → 2. 웹 스토리지 → 3. 캐시/SW
 *   4. 파일계 → 5. IndexedDB → 6. 서버 요청
 *   7. (선택) 새로고침
 * 
 * @param {Object} options
 * @param {boolean} options.useServerHeader - 서버 Clear-Site-Data 요청 여부 (기본: true)
 * @param {boolean} options.dropFileManagerDB - fileManagerDB 삭제 여부 (기본: true)
 * @param {boolean} options.dropAllIndexedDB - 모든 IndexedDB 삭제 여부 (기본: false)
 * @param {boolean} options.autoReload - 완료 후 자동 새로고침 (기본: true)
 * 
 * @returns {Promise<Object>} 각 저장소별 삭제 결과
 * 
 * @example
 * // 기본 사용 (모든 저장소 삭제 후 새로고침)
 * await nukeAll();
 * 
 * @example
 * // 새로고침 없이 삭제만
 * const results = await nukeAll({ autoReload: false });
 * console.log('삭제 결과:', results);
 * 
 * @example
 * // 모든 IndexedDB 포함 삭제
 * await nukeAll({ dropAllIndexedDB: true });
 * 
 * @example
 * // 로그아웃 시 (서버 요청 포함)
 * await nukeAll({ useServerHeader: true });
 */
export async function nukeAll(options = {}) {
  const {
    useServerHeader = true,
    dropFileManagerDB = true,
    dropAllIndexedDB = false,
    autoReload = true,
  } = options;

  const results = {};

  try {
    // 1. 쿠키 (가장 먼저 - 인증 정보)
    results.cookies = await clearCookiesAggressive();

    // 2. 웹 스토리지
    results.localStorage = await clearLocal();
    results.sessionStorage = await clearSession();
    results.sharedStorage = await clearSharedStorage();

    // 3. 캐시 & Service Worker
    results.cache = await clearCacheStorage();
    results.serviceWorker = await unregisterServiceWorkersAndPush();

    // 4. 파일계 저장소
    results.opfs = await clearOPFS();
    results.storageBuckets = await clearStorageBuckets();

    // 5. IndexedDB (옵션에 따라)
    if (dropAllIndexedDB) {
      results.indexedDB = await clearAllIndexedDB();
    } else if (dropFileManagerDB) {
      results.indexedDB = await clearAppIndexedDB();
    }

    // 6. 서버 측 Clear-Site-Data (옵션)
    if (useServerHeader) {
      results.server = await clearViaServer();
    }

    logInfo('✅ nukeAll 완료', results);

    // 7. 자동 새로고침 (옵션)
    if (autoReload) {
      setTimeout(() => window.location.reload(true), 100);
    }

    return results;
  } catch (err) {
    logError('nukeAll 실패', err);
    throw err;
  }
}

/* =========================================================
 * 🎯 용도별 통합 함수
 * ======================================================= */

/**
 * 사용자 데이터만 삭제 (캐시/SW 유지)
 * 
 * @description
 * 로그아웃 시 사용 - 앱 캐시는 유지하고 사용자 데이터만 삭제
 * 
 * 삭제 대상:
 * - 쿠키 (인증 토큰 포함)
 * - localStorage, sessionStorage
 * - SharedStorage, OPFS, Storage Buckets
 * - 앱 IndexedDB
 * 
 * 유지 대상:
 * - CacheStorage (정적 파일 캐시)
 * - Service Worker
 * 
 * @param {Object} options
 * @param {boolean} options.useServerHeader - 서버 요청 여부 (기본: false)
 * @returns {Promise<Object>} 삭제 결과
 * 
 * @example
 * // 로그아웃 핸들러에서
 * async function logout() {
 *   await api.post('/auth/logout');
 *   await clearUserDataOnly();
 *   router.push('/login');
 * }
 */
export async function clearUserDataOnly(options = {}) {
  const { useServerHeader = false } = options;
  const results = {};

  results.cookies = await clearCookiesAggressive();
  results.localStorage = await clearLocal();
  results.sessionStorage = await clearSession();
  results.sharedStorage = await clearSharedStorage();
  results.opfs = await clearOPFS();
  results.storageBuckets = await clearStorageBuckets();
  results.indexedDB = await clearAppIndexedDB();

  if (useServerHeader) {
    results.server = await clearViaServer();
  }

  logInfo('✅ 사용자 데이터 삭제 완료', results);
  return results;
}

/**
 * 캐시와 Service Worker만 삭제
 * 
 * @description
 * PWA 업데이트 문제 해결 시 사용
 * - 오래된 캐시로 인한 버그
 * - SW 업데이트 미반영
 * 
 * @returns {Promise<Object>} 삭제 결과
 * 
 * @example
 * // 설정 페이지의 "캐시 삭제" 버튼
 * <button @click="handleClearCache">캐시 삭제</button>
 * 
 * async function handleClearCache() {
 *   await clearCacheAndSw();
 *   toast.success('캐시가 삭제되었습니다.');
 *   window.location.reload();
 * }
 */
export async function clearCacheAndSw() {
  const results = {};

  results.serviceWorker = await unregisterServiceWorkersAndPush();
  results.cache = await clearCacheStorage();

  logInfo('✅ 캐시/SW 삭제 완료', results);
  return results;
}

/**
 * 웹 스토리지만 삭제
 * 
 * @description
 * localStorage, sessionStorage, SharedStorage만 삭제
 * 가벼운 초기화가 필요할 때 사용
 * 
 * @returns {Promise<Object>} 삭제 결과
 * 
 * @example
 * // 설정 초기화
 * await clearWebStorage();
 */
export async function clearWebStorage() {
  const results = {};

  results.localStorage = await clearLocal();
  results.sessionStorage = await clearSession();
  results.sharedStorage = await clearSharedStorage();

  logInfo('✅ 웹 스토리지 삭제 완료', results);
  return results;
}

/**
 * 파일계 저장소만 삭제
 * 
 * @description
 * OPFS, Storage Buckets만 삭제
 * 대용량 파일 정리 시 사용
 * 
 * @returns {Promise<Object>} 삭제 결과
 * 
 * @example
 * // 임시 파일 정리
 * await clearFileLikeStorage();
 */
export async function clearFileLikeStorage() {
  const results = {};

  results.opfs = await clearOPFS();
  results.storageBuckets = await clearStorageBuckets();

  logInfo('✅ 파일계 저장소 삭제 완료', results);
  return results;
}

/* =========================================================
 * 📤 Default Export
 * ======================================================= */

/**
 * cacheBuster 모듈 전체 내보내기
 * 
 * @example
 * import cacheBuster from '@/utils/cacheBuster';
 * 
 * // 객체로 접근
 * await cacheBuster.nukeAll();
 * await cacheBuster.clearUserDataOnly();
 * 
 * // 또는 개별 import
 * import { nukeAll, checkAndClearCache } from '@/utils/cacheBuster';
 */
const cacheBuster = Object.freeze({
  // 버전 체크
  checkAndClearCache,
  
  // 개별 저장소 삭제
  clearCookiesAggressive,
  clearLocal,
  clearSession,
  clearSharedStorage,
  clearCacheStorage,
  unregisterServiceWorkersAndPush,
  clearOPFS,
  clearStorageBuckets,
  clearIndexedDBStores,
  clearAppIndexedDB,
  clearAllIndexedDB,
  clearViaServer,
  
  // 통합 함수
  nukeAll,
  clearUserDataOnly,
  clearCacheAndSw,
  clearWebStorage,
  clearFileLikeStorage,
  
  // 상수 (외부에서 참조 가능)
  BUILD_VERSION,
  APP_IDB_NAMES,
});

export default cacheBuster;